// ============================================================================
// src/app/navigation/viewRoutes.ts — the view ↔ route-table bridge.
// ============================================================================
// B3.1 finish. Domain entries now match with real <Route> elements, which match
// on a LOCATION. The application still decides what to render from the `view`
// state machine, and `view` leads the URL by one render: setView('X') renders
// immediately, and the URL↔view effect in App navigates to VIEW_TO_PATH[X]
// afterwards. Matching a domain's routes against the raw location during that
// frame would mount whatever the OLD path resolves to — a wrong mount and its
// effects, once per navigation, not a cosmetic flicker.
//
// So an entry matches against a ROUTED location:
//
//   the real location   when it already belongs to the view being rendered
//                       (steady state — record sub-paths, search and hash all
//                       survive exactly as the component sees them today)
//   the view's own path when it does not (the transition frame — renders what
//                       `view === 'X'` rendered, one frame earlier than the URL)
//
// The single rule is `routedLocationFor`, and it doubles as the domain
// predicate: null means "not this domain's view", which is the fall-through the
// resolver chain used to express by returning null.
//
// Nothing here imports React or react-router: it is data + string logic, unit
// tested directly, and importable by a route oracle without booting the app.
// ============================================================================

// One row of a domain's route table: the view it serves and every path that
// view owns, `paths[0]` being the canonical one. This is the shape the seven
// tables (src/app/appRoutes.ts and the six domain tables) are written in.
export interface RouteRow {
  view: string;
  paths: string[];
}

// What a caller may hand to routedLocationFor as the current location: a real
// driver location, a bare pathname string, or nothing at all during a frame
// that has none. Only `pathname` is ever read.
export type RoutedLocationInput = string | {pathname?: string} | null | undefined;

// '/cattle/dailys/*' -> '/cattle/dailys'. A splat pattern's base is the path
// the view itself is reachable at; the splat is the record space beneath it.
export function routePatternBase(pattern: string): string {
  return pattern.endsWith('/*') ? pattern.slice(0, -2) : pattern;
}

// Does `pathname` belong to `pattern`, by the same rule the URL→view adapter
// uses? A splat pattern owns its base and everything under it; a plain pattern
// owns only itself. The distinction is load-bearing: '/cattle' must NOT claim
// '/cattle/herds', or a cattleHome render would resolve to the herds route.
// `pathname` is `unknown` because the "not a string" answer is part of the
// contract: a missing or non-string pathname is false, never a throw.
export function routePatternOwns(pattern: string, pathname: unknown): boolean {
  if (typeof pathname !== 'string' || !pathname) return false;
  const base = routePatternBase(pattern);
  if (pathname === base) return true;
  return pattern.endsWith('/*') && pathname.startsWith(base + '/');
}

// Every view a table claims, in table order.
export function routeTableViews(table: readonly RouteRow[]): string[] {
  return table.map((row) => row.view);
}

// The row a view is served by, or null when the table does not claim it.
export function routeRowFor(view: string, table: readonly RouteRow[]): RouteRow | null {
  return table.find((row) => row.view === view) || null;
}

// Is `pathname` claimed exactly by some OTHER row in the table?
//
// One domain nests a dedicated view inside another view's record space:
// '/dailys/tasks' is tasksWebform, and it sits under webformhub's '/dailys/*'.
// Prefix ownership alone would say webformhub owns '/dailys/tasks', so a
// transition frame heading TO the hub from the tasks form would keep the real
// location, and the router -- which ranks a static segment above a splat --
// would render the tasks form for that frame instead of the hub.
//
// The URL→view adapter in App expresses the same precedence as
// `!exactPathView && pathname.startsWith(...)`: an exact path entry always
// beats the generic sub-path fallback. This is that rule, on this side.
function claimedExactlyByAnotherRow(view: string, pathname: unknown, table: readonly RouteRow[]): boolean {
  return table.some(
    (row) => row.view !== view && row.paths.some((pattern) => !pattern.endsWith('/*') && pattern === pathname),
  );
}

// The location a domain entry should match against for `view`, or null when
// this table does not own `view` at all (the caller falls through, exactly as
// the resolver chain did).
// Generic in the location so a steady-state match hands back the CALLER'S OWN
// object, identity intact — the property every consumer depends on. The other
// two outcomes are a canonical path string and null.
export function routedLocationFor<L extends RoutedLocationInput>(
  view: string,
  location: L,
  table: readonly RouteRow[],
): L | string | null {
  const row = routeRowFor(view, table);
  if (!row) return null;
  const candidate: RoutedLocationInput = location;
  const pathname = candidate && typeof candidate === 'object' ? candidate.pathname : candidate;
  const owned =
    row.paths.some((pattern) => routePatternOwns(pattern, pathname)) &&
    !claimedExactlyByAnotherRow(view, pathname, table);
  if (owned) return location;
  return routePatternBase(row.paths[0]);
}
